// PageHeader — the top of every ERP list page, as one component.
//
// Six pages each drew their own header and, having nothing to copy from, all
// converged on the weakest form: a gold h1 beside a gold button, three styles of
// secondary action, and a paragraph of explanation under the title that nobody
// reads twice. The event page (Events/EventDetail.js) had the strong form all
// along, so this is that composition made shareable: an accent rule, one big
// title in the text color, the `?` help drawer beside it, one filled action,
// and a quiet row of the others.
//
// Gold is spent in two places and two only: the accent rule (or the domain
// icon that replaces it) and the primary action. That is the whole reason the title is white. A page whose title,
// button and links are all the accent color has no loud thing, because
// everything is.
//
// The explanation a page used to print under its title belongs in `help`. The
// header takes at most one sentence of `lede`; if it needs a second, the second
// is help.

import React from 'react';
import { Link } from 'react-router-dom';
import BackLink from './BackLink';
import HelpDrawer from './HelpDrawer';
import { Menu, MenuItem } from './ui/Menu';
import { buttonClass } from './ui/Button';

export interface PageHeaderProps {
  /** The h1. Short: two or three words. A node (an inline-editable title) must pass `helpTitle` if it has help. */
  title: React.ReactNode;
  /** Body of the `?` drawer. Pages keep their explanation here, not under the title. */
  help?: React.ReactNode;
  /** The drawer's heading. Defaults to "About {title}". */
  helpTitle?: string;
  /** One sentence under the title, in the quiet text color. Two sentences is help. */
  lede?: React.ReactNode;
  /** The one filled action: a `PrimaryAction`. */
  primary?: React.ReactNode;
  /** `SecondaryAction`s, rendered after the primary in the same row. */
  actions?: React.ReactNode;
  /** A row under the title for chips a page is already showing there. */
  meta?: React.ReactNode;
  /**
   * Commands about the whole list rather than one row of it (Export CSV),
   * behind one tonal "Tools" trigger at the end of the action row. Rendered
   * only when there is at least one. A tool is a thing done to what the page
   * is showing; a way of starting new work is a `SecondaryAction`.
   */
  tools?: MenuItem[];
  /** The page this one sits under, as a pill above the title ("‹ Spark"). Detail pages set it; list pages do not. */
  back?: { to: string; label: string };
  /**
   * The domain's mark beside the title, in gold, standing in for the accent rule: a page with
   * an icon draws no rule. A kit icon from `ui/icons`; the header sets its size and colour.
   */
  icon?: React.ReactNode;
  className?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  help,
  helpTitle,
  lede,
  primary,
  actions,
  meta,
  tools,
  back,
  icon,
  className = '',
}) => (
  <header className={`relative pt-3 ${className}`}>
    {/* The accent rule: the event header's 3px gradient, and the first of the
        two places gold is allowed on this surface. A width, not the full
        content width, so under the nav it reads as the start of a page and not
        as a second nav edge. */}
    {!icon && (
      <div
        aria-hidden="true"
        className="absolute left-0 top-0 h-[3px] w-40 sm:w-56 rounded-full bg-gradient-to-r from-primary/80 via-primary/40 to-transparent"
      />
    )}
    {back && <BackLink to={back.to} label={back.label} className="mb-2" />}
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2.5">
          {icon && (
            <span
              aria-hidden="true"
              className="mr-0.5 flex flex-none text-primary drop-shadow-glow [&>svg]:h-7 [&>svg]:w-7 sm:[&>svg]:h-8 sm:[&>svg]:w-8"
            >
              {icon}
            </span>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight text-on-surface text-balance">
            {title}
          </h1>
          {help && <HelpDrawer title={helpTitle ?? (typeof title === 'string' ? `About ${title}` : 'About this page')}>{help}</HelpDrawer>}
        </div>
        {lede && <p className="mt-1 max-w-2xl text-pretty text-sm text-on-surface-variant">{lede}</p>}
        {meta && <div className="mt-2.5 flex flex-wrap items-center gap-2">{meta}</div>}
      </div>
      {(primary || actions || (tools && tools.length > 0)) && (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-x-2 gap-y-2 sm:justify-end sm:pt-0.5">
          {primary}
          {actions}
          {tools && tools.length > 0 && <Menu label="Tools" variant="tonal" items={tools} />}
        </div>
      )}
    </div>
  </header>
);

export default PageHeader;

// ── Actions ──────────────────────────────────────────────────────────────────
//
// Three tiers, Material's three: filled, tonal, text. The page's one
// consequential act is filled; a real alternative that starts a task is tonal;
// a place to go and look is text. A page picks one filled action or none. Two
// filled pills side by side is two pages sharing a header.

type ActionTarget =
  | { to: string; onClick?: never }
  | { onClick: () => void; to?: never };

type ActionProps = ActionTarget & {
  children: React.ReactNode;
  disabled?: boolean;
  /** aria-label, when the visible text needs more context for a reader. */
  label?: string;
  className?: string;
};

// The kit's keys, machined: header actions are buttons like any other (6px, never pills).
const PRIMARY = buttonClass('filled');
const TONAL = buttonClass('tonal');
const TEXT = buttonClass('quiet');

/**
 * A Link when it goes somewhere, a button when it does something. Same look
 * either way: a person reading the header should not be able to tell which of
 * "+ New item" and "From a document" opens a modal and which changes the page,
 * because that is not a fact about their task.
 */
const Action: React.FC<ActionProps & { base: string }> = ({
  to,
  onClick,
  children,
  disabled,
  label,
  className = '',
  base,
}) => {
  const cls = `${base} ${className}`;
  if (to !== undefined) {
    // A disabled link is not a thing; render a disabled button in its place so
    // the row keeps its shape.
    if (disabled) {
      return (
        <button type="button" disabled aria-label={label} className={cls}>
          {children}
        </button>
      );
    }
    return (
      <Link to={to} aria-label={label} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} className={cls}>
      {children}
    </button>
  );
};

/** The one filled action on a page. */
export const PrimaryAction: React.FC<ActionProps> = (props) => <Action {...props} base={PRIMARY} />;

/**
 * Everything else in the row. `tonal` (the default) for another way to start
 * work; `text` for a place to go and look.
 */
export const SecondaryAction: React.FC<ActionProps & { variant?: 'tonal' | 'text' }> = ({
  variant = 'tonal',
  ...props
}) => <Action {...props} base={variant === 'tonal' ? TONAL : TEXT} />;
